import React from 'react'
import { Link, useLocation } from 'react-router-dom'

/**
 * FooterV2 - Barista Map style dark footer
 * - Black background with white text (color-scheme-5 equivalent)
 * - Three-column layout: Menu_1 / Menu_2 / Address & Contact
 * - Bottom bar: copyright (left) + social text links (right)
 * - Container: max-w-[1600px] aligned with HeaderV2 / V2 sections
 * - Used only on /zh/v2 and /en/v2 (HomePageV2)
 */
const FooterV2: React.FC = () => {
  const location = useLocation()
  const currentLang: 'zh' | 'en' = location.pathname.startsWith('/en') ? 'en' : 'zh'

  const t =
    currentLang === 'zh'
      ? {
          menu1Title: 'Footer Menu 1',
          menu2Title: 'Footer Menu 2',
          addressTitle: '',
          menu1: [
            { label: 'Home', to: `/${currentLang}/v2` },
            { label: 'About', to: `/${currentLang}/lab` },
            { label: 'Our Business', to: `/${currentLang}/wholesale` },
            { label: 'Shop', to: `/${currentLang}/shop` },
            { label: 'Wholesale', to: `/${currentLang}/wholesale` },
          ],
          menu2: [
            { label: 'Lab', to: `/${currentLang}/lab` },
            { label: 'Stores', to: `/${currentLang}/store` },
            { label: 'Contact', to: `/${currentLang}/faq` },
            { label: 'Privacy Policy', to: `/${currentLang}/privacy` },
            { label: 'Terms of Service', to: `/${currentLang}/terms` },
          ],
          postal: '〒110',
          address: '台北市信義區信義路四段389號1樓',
          email: 'service@ongucoffee.com',
          copyright: '© 2025',
          brand: 'ONGU STUDIO Coffee Roasters',
        }
      : {
          menu1Title: 'Footer Menu 1',
          menu2Title: 'Footer Menu 2',
          addressTitle: '',
          menu1: [
            { label: 'Home', to: `/${currentLang}/v2` },
            { label: 'About', to: `/${currentLang}/lab` },
            { label: 'Our Business', to: `/${currentLang}/wholesale` },
            { label: 'Shop', to: `/${currentLang}/shop` },
            { label: 'Wholesale', to: `/${currentLang}/wholesale` },
          ],
          menu2: [
            { label: 'Lab', to: `/${currentLang}/lab` },
            { label: 'Stores', to: `/${currentLang}/store` },
            { label: 'Contact', to: `/${currentLang}/faq` },
            { label: 'Privacy Policy', to: `/${currentLang}/privacy` },
            { label: 'Terms of Service', to: `/${currentLang}/terms` },
          ],
          postal: '110',
          address: '1F, No.389, Sec.4, Xinyi Rd., Xinyi Dist., Taipei City',
          email: 'service@ongucoffee.com',
          copyright: '© 2025',
          brand: 'ONGU STUDIO Coffee Roasters',
        }

  const headingStyle: React.CSSProperties = {
    fontFamily: "'EB Garamond', 'Noto Serif TC', serif",
    fontWeight: 400,
    letterSpacing: '0.25em',
  }

  const linkStyle: React.CSSProperties = {
    fontFamily: "'Noto Serif TC', 'Noto Sans TC', sans-serif",
    fontWeight: 300,
  }

  return (
    <footer className="bg-black text-white">
      <div className="mx-auto max-w-[1600px] px-6 md:px-10">
        {/* Top: 3-column grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-12 md:gap-16 py-16 md:py-24">
          {/* Menu 1 */}
          <div>
            <h3
              className="text-[11px] uppercase mb-6 text-white/60"
              style={headingStyle}
            >
              {t.menu1Title}
            </h3>
            <ul className="space-y-3">
              {t.menu1.map((item) => (
                <li key={item.label}>
                  <Link
                    to={item.to}
                    onClick={() => window.scrollTo(0, 0)}
                    className="text-sm text-white/85 hover:text-white transition-colors duration-200"
                    style={linkStyle}
                  >
                    {item.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Menu 2 */}
          <div>
            <h3
              className="text-[11px] uppercase mb-6 text-white/60"
              style={headingStyle}
            >
              {t.menu2Title}
            </h3>
            <ul className="space-y-3">
              {t.menu2.map((item) => (
                <li key={item.label}>
                  <Link
                    to={item.to}
                    onClick={() => window.scrollTo(0, 0)}
                    className="text-sm text-white/85 hover:text-white transition-colors duration-200"
                    style={linkStyle}
                  >
                    {item.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Address / Contact */}
          <div className="space-y-2 text-sm text-white/85" style={linkStyle}>
            <div>{t.postal}</div>
            <div>{t.address}</div>
            <a
              href={`mailto:${t.email}`}
              className="block hover:text-white transition-colors duration-200"
            >
              {t.email}
            </a>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="border-t border-white/15 py-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div
            className="text-xs text-white/70"
            style={linkStyle}
          >
            {t.copyright}{' '}
            <Link
              to={`/${currentLang}/v2`}
              className="hover:text-white transition-colors duration-200"
            >
              {t.brand}
            </Link>
          </div>

          <div className="flex items-center gap-6 text-xs text-white/70" style={linkStyle}>
            <a
              href="https://www.facebook.com/ongustudio/"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-white transition-colors duration-200"
            >
              Facebook
            </a>
            <a
              href="https://www.instagram.com/onguroasters/"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-white transition-colors duration-200"
            >
              Instagram
            </a>
            <a
              href="https://line.me/R/ti/p/@993lrijj"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-white transition-colors duration-200"
            >
              LINE
            </a>
          </div>
        </div>
      </div>
    </footer>
  )
}

export default FooterV2
